import React, { FC, useState } from 'react';
import { TGroupingItem, TObjectionTypes } from 'domains/ReportBuilder/types';
import { ReactComponent as ListEnterSvg } from 'assets/list-enter.svg';
import TooltipPoratal from 'components/UI/TooltipPoratal';
import Loader from 'components/UI/Loader';
import { isObjectionData } from 'domains/ReportBuilder/utils';
import { useTranslation } from 'react-i18next';
import { checkedLocalization } from 'utils/checkedLocalization';
import ReportSelect from '../../ReportSelect';
import HintBlock from '../../HintBlock';
import css from '../../../styles.module.scss';
import { RenderDynamicCatalog } from '../../RenderFilterType/RenderDynamicCatalog/RenderDynamicCatalog';
import { RenderNo } from '../../RenderFilterType/RenderNo';
import { RenderTarget } from '../../RenderFilterType/RenderTarget';
import { IReportFiltersProps } from './types';
import { useReportFilters } from './hooks/useReportFilters';
import { RenderCatalogFiltering } from '../../RenderFilterType/RenderDynamicCatalog/RenderCatalogFiltering';

const ReportFilters: FC<IReportFiltersProps> = ({
  containerRef,
  formik,
  filterBy,
  objectionBy,
  filterings,
  objections,
  currentUserStep,
  setObjections,
  stateOfPanel,
}) => {
  const { t } = useTranslation();
  const [openedFilter, setOpenedFilter] = useState<string | null>(null);

  const {
    isNextStepLoading,
    showStepBtn,
    noTypeLastSort,
    getCurrentHint,
    lastObjectionKey,
    handleSubmit,
    closeAllSelects,
    getSelectRef,
  } = useReportFilters(
    formik,
    objectionBy,
    objections,
    filterings,
    setObjections,
    stateOfPanel,
    currentUserStep,
    containerRef,
  );

  const renderSubmitBtn = () =>
    isNextStepLoading ? (
      <Loader width={20} height={20} />
    ) : (
      <TooltipPoratal
        hint={t(`confirm_btn`)}
        classname={css.startSelect__btn}
        hintClassName="report_confirm_btn"
        onClick={() => {
          closeAllSelects();
          handleSubmit();
        }}
      >
        <ListEnterSvg />
      </TooltipPoratal>
    );

  const renderFiltering = (key: string, filteringItem: TGroupingItem) => {
    if (!filteringItem) return null;

    const { type } = filteringItem;

    const currentValue = filterBy[key];

    const selectRef = getSelectRef(`filtering-${key}`);

    if (type === 'catalog') {
      return (
        <RenderCatalogFiltering
          item={filteringItem}
          formikKey="filterBy"
          currentKey={key}
          selectRef={selectRef}
          openedFilter={openedFilter}
          setOpenedFilter={setOpenedFilter}
        />
      );
    }

    if (type === 'target') {
      return (
        <RenderTarget
          item={filteringItem}
          formikKey="filterBy"
          currentKey={key}
          selectRef={selectRef}
          openedFilter={openedFilter}
          setOpenedFilter={setOpenedFilter}
        />
      );
    }

    if (type === 'no') {
      return (
        <RenderNo
          formik={formik}
          item={filteringItem}
          currentValue={currentValue}
          formikKey="filterBy"
          currentKey={key}
        />
      );
    }

    if (type === 'dynamic_catalog') {
      return (
        <RenderDynamicCatalog
          item={filteringItem}
          currentValue={currentValue}
          formikKey="filterBy"
          currentKey={key}
          source={filteringItem.source || ''}
          formik={formik}
        />
      );
    }

    return null;
  };

  return (
    <>
      {Object.entries(objections).map(([objectionKey, objectionValue]) => {
        if (!isObjectionData(objectionValue?.data)) {
          return null;
        }

        const ObjectionOptionsData = objectionValue.data.map((item) => ({
          label: item.title,
          value: item.internal_id,
          id: item.xxhash,
        }));

        const isLastObjection = objectionKey === lastObjectionKey;

        const showHint =
          !isNextStepLoading &&
          isLastObjection &&
          objectionKey !== 'creative' &&
          stateOfPanel;

        const selectRef = getSelectRef(`objection-${objectionKey}`);
        const selectLabel = checkedLocalization(
          `reportbuilder.filters.${objectionValue.label}`,
          objectionValue.label,
          false,
        );

        return (
          <HintBlock
            key={objectionKey}
            showHint={showHint}
            hintText={getCurrentHint}
            placement="left"
            containerRef={containerRef}
            offset={34}
            offsetTop={10}
          >
            <div className={css.startSelect}>
              <ReportSelect
                key={objectionKey}
                formikKey="objectionBy"
                options={ObjectionOptionsData}
                label={selectLabel}
                currentKey={objectionKey}
                selectRef={selectRef}
                openedFilter={openedFilter}
                setOpenedFilter={setOpenedFilter}
                showSubtitleInOption
              />
              {objectionKey !== 'creative' &&
                showStepBtn[objectionKey as TObjectionTypes] &&
                renderSubmitBtn()}
            </div>
          </HintBlock>
        );
      })}

      {noTypeLastSort.map(([key, value]) => (
        <React.Fragment key={key}>{renderFiltering(key, value)}</React.Fragment>
      ))}
    </>
  );
};

export default ReportFilters;
